/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { isLoggedIn, setQuestionAnswerToLocalStorage } from '../../../../core/localStorage';
import { Question } from './questionData';
import { useCreateNewSurveyAnswer, useQuestionsData, useUpdateSurveyAnswer } from '../../../../core/api';
import SurveyAnswer from '../../../../models/SurveyAnswer';
import {
	answerSplitCharacter,
	bbocProfilePageUrl,
	bbocQuestionsPageUrl,
	bbocResultPageUrl,
	otherFieldName,
	otherOptionValue,
	questionFieldName
} from '../../../../core/constants';
import QuestionField from './QuestionField';
import { QuestionTypes } from '../../../../core/enums';
import { evaluateVisibility } from '../../../../core/domain';
import { pixelsToNumber, scrollToRef } from '../../../../lib/utils';
import { appHeaderHeight } from '../../../cssConstants';
import { useNavigateWithAppName } from '../../../common/NavigateWithAppName';
import AppForm from '../../common/AppForm';

const QuestionForm = (props: Props) => {
	const navigate = useNavigateWithAppName();
	const questionsData = useQuestionsData() ?? [];
	const formButtonRef = React.useRef(null);
	const queryClient = useQueryClient();

	const { questionId, surveyUniqueId, surveyAnswer } = props;

	const questionSpec = questionsData[questionId - 1];
	const defaultValues = {
		[questionFieldName]: surveyAnswer.answer,
		other: getOtherInputValue(questionSpec, surveyAnswer.answer)
	};

	const submitSuccessCallback = (answerValues: any) => {
		if (questionSpec.saveAnswerToLocal)
			setQuestionAnswerToLocalStorage(questionSpec.uniqueName, answerValues[questionFieldName]);

		// To set the loading state in correct status for next question page by using setTimeout with 0 to append the code to Javascript's event queue.
		setTimeout(() => navigate(handleUrlRedirection(questionId, false, surveyUniqueId, questionsData)), 0);
	};

	const updateSurveyAnswerMutation = useUpdateSurveyAnswer(queryClient, (answerValues: any) =>
		submitSuccessCallback(answerValues)
	);
	const createSurveyAnswerMutation = useCreateNewSurveyAnswer(queryClient, (answerValues: any) =>
		submitSuccessCallback(answerValues)
	);
	const handleSubmit = async (data: any) => {
		const answerValues: any = JSON.parse(JSON.stringify(data));
		if (answerValues[otherFieldName]) {
			if (questionSpec.type === QuestionTypes.RadioList) answerValues[questionFieldName] = answerValues[otherFieldName];
			else if ([QuestionTypes.DragAndDrop, QuestionTypes.CheckList].includes(questionSpec.type)) {
				const answerArray: string[] = answerValues[questionFieldName].split(answerSplitCharacter);
				// Initial value of empty 'other' input is 'other'(otherOptionValue) from the questionsData option,
				// existing saved 'other' input value is the exact string user typed in.
				const otherFieldValueIndex =
					answerArray.indexOf(otherOptionValue) === -1
						? answerArray.indexOf(getOtherInputValue(questionSpec, surveyAnswer.answer))
						: answerArray.indexOf(otherOptionValue);
				answerArray.splice(otherFieldValueIndex, 1, answerValues[otherFieldName]);
				answerValues[questionFieldName] = answerArray.join(answerSplitCharacter);
			}
		}
		const score = getScore(questionSpec, answerValues[questionFieldName]);
		const updatedSurveyAnswer = { ...surveyAnswer, answer: answerValues[questionFieldName], score };
		const model = { surveyUniqueId, surveyAnswer: updatedSurveyAnswer, answerValues };
		if (updatedSurveyAnswer.clientSurveyAnswerId) {
			updateSurveyAnswerMutation.mutate(model);
		} else {
			createSurveyAnswerMutation.mutate(model);
		}
	};

	const handleBackButtonClick = () => {
		navigate(handleUrlRedirection(questionId, true, surveyUniqueId, questionsData));
	};

	const handleRadioQuestionChange = () => {
		scrollToRef(formButtonRef, pixelsToNumber(appHeaderHeight));
	};

	return (
		<AppForm
			defaultValues={defaultValues}
			onSubmit={handleSubmit}
			onBackButtonClick={!(isLoggedIn() && questionId === 1) ? handleBackButtonClick : null} // App user can't go back to welcome page.
			primaryButtonText="Next"
			singleColumn
			formButtonRef={formButtonRef}
		>
			{questionSpec && <QuestionField questionSpec={questionSpec} onRadioQuestionChange={handleRadioQuestionChange} />}
		</AppForm>
	);
};

export default QuestionForm;

interface Props {
	questionId: number;
	surveyUniqueId: string;
	surveyAnswer: SurveyAnswer;
}

const getScore = (questionSpec: Question, answer: string) => {
	let totalScore = 0;
	switch (questionSpec.type) {
		case QuestionTypes.RadioList:
			return questionSpec.options.find((x) => x.value === answer)?.score;
		case QuestionTypes.CheckList:
			totalScore = questionSpec.options
				.filter((x) => answer.includes(x.value as string))
				.reduce((total, current) => total + current.score, 0);
			totalScore = Math.min(totalScore, 100);
			totalScore += getBonusScore(questionSpec, answer);
			return totalScore;
		case QuestionTypes.Slider: // Slider saves label text as answer as value is meaningless.
			return questionSpec.options.find((x) => x.label === answer)?.score;
		case QuestionTypes.DragAndDrop: // Currently, no score for DND question type.
			return null;
		default:
			throw new Error(`Unknown question type: ${questionSpec.type}`);
	}
};

const getBonusScore = (questionSpec: Question, answer: string) =>
	answer.split(answerSplitCharacter).length >= questionSpec.bonusScore?.atLeastSelected
		? questionSpec.bonusScore?.score
		: 0;

const getOtherInputValue = (questionSpec: Question, fieldValue: string) => {
	if (questionSpec?.type === QuestionTypes.RadioList) {
		return !questionSpec.options.some((x) => x.value === fieldValue) ? fieldValue : '';
	}
	if ([QuestionTypes.DragAndDrop, QuestionTypes.CheckList].includes(questionSpec?.type)) {
		const selectedValueData = fieldValue?.split(answerSplitCharacter).filter((x) => x !== '');
		return selectedValueData?.find((x) => !questionSpec.options.some((y) => y.value === x));
	}
	return null;
};

const handleUrlRedirection = (
	currentQuestionId: number,
	isBack: boolean,
	surveyUniqueId: string,
	questionsData: Question[]
): string => {
	// questionId in url is the question index + 1 in questionsData, so must be between 1 and questionsData.length.
	const newQuestionId = isBack ? currentQuestionId - 1 : currentQuestionId + 1;
	if (newQuestionId < 1) return bbocProfilePageUrl;
	if (newQuestionId > questionsData.length) return `${bbocResultPageUrl}?surveyUniqueId=${surveyUniqueId}`;

	const newQuestionSpec = questionsData[newQuestionId - 1];
	const isNewQuestionVisible = evaluateVisibility(newQuestionSpec.visible);
	if (isNewQuestionVisible) return `${bbocQuestionsPageUrl}/${newQuestionId}`;

	return handleUrlRedirection(newQuestionId, isBack, surveyUniqueId, questionsData);
};
